import type React from 'react';
import { useContext, useMemo, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { Anchor, DetailedIconTrophy, Flex, Spacer, Tooltip, TooltipPopper } from '@zillow/constellation';
import { Button, Paragraph, Text, Toast, useToast } from '@zillow/constellation-10';
import { useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { RMWContext } from '../../../../providers/RMWContext';

import { RoutingSelectors, UserManagementSelectors } from '../../../../selectors';
import { useSelector } from '../../../../selectors/useSelector';

import { useGetNextStepsSummary } from '../../../../hooks/queries/summary';

import PropertiesActions from '../../../../actions/PropertiesActions';

import { VERTICAL_DISPLAY_TYPE } from '../../../../constants/PropertiesConstants';

import ApplicationsCSEvents from '../../../../model/ClickstreamEvent/ApplicationsCSEvents';

import ListingActivationTerm from '../../../ListingActivationTerms';
import AlertWithTitle from '../../../AlertWithTitle';
import SimpleCard from '../../../SimpleCard';

import {
  StyledEmptyIconContainer,
  StyledEmptySimpleCard,
  StyledEmptyStateContainer,
} from '../../../../StyledComponents';
import { EmptyStateBodyText, EmptyStateTitleText } from '../../../../styles';

import ApplicationShareLinks from '../ApplicationShareLinks';

import { StyledCTAContainerSpacer } from './StyledComponents';

import type { ApplicationsSummary } from '../../interfaces';
import type { TRIGGER_LOCATIONS } from '../../../../constants/ClickstreamConstants';

type Props = {
  canEnableApplications: boolean;
  displayType?: VERTICAL_DISPLAY_TYPE;
  setShowAppsPermissionModal: (value: boolean) => void;
  applicationsSummary?: ApplicationsSummary;
  triggerLocation: (typeof TRIGGER_LOCATIONS)[keyof typeof TRIGGER_LOCATIONS];
};

const accessText =
  "You need permission to enable and access applications. Request access from your account's application manager.";

const ApplicationsDisabledCard = ({
  canEnableApplications,
  triggerLocation,
  displayType = VERTICAL_DISPLAY_TYPE.FULL,
  setShowAppsPermissionModal,
  applicationsSummary,
}: Props) => {
  const dispatch = useDispatch();
  const { enqueueToast } = useToast();
  const queryClient = useQueryClient();

  const {
    paths: { tenantScreeningPage },
  } = useContext(RMWContext);

  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));
  const isApplicationManager = useSelector((state) => UserManagementSelectors.makeApplicationManager(state));

  const { canAccessBusinessVerificationLite, listingAlias, status, user } = useSelector(
    (state) => ({
      canAccessBusinessVerificationLite: state?.user?.features?.canAccessBusinessVerificationLite,
      listingAlias: state?.properties?.propertyOverview?.listingAlias,
      status: state?.properties?.propertyOverview?.status,
      user: state?.user,
    }),
    shallowEqual,
  );

  const [isEnableApplicationsLoading, setIsEnableApplicationsLoading] = useState(false);
  const [failureBanner, setFailureBanner] = useState<{ body: React.ReactNode | null; title: string | null }>({
    body: null,
    title: null,
  });

  const { refetch: refetchGetNextStepsSummary } = useGetNextStepsSummary(listingAlias);
  const { refetch: refetchWebAnalytics } = useRentalManagerUserPropertyWebAnalyticsWebApiHandler_get(
    {
      listingAlias,
      includeLeases: false,
    },
    { enabled: Boolean(listingAlias) },
  );

  const alignItems = displayType === VERTICAL_DISPLAY_TYPE.FULL ? 'center' : 'left';
  const displayIconTrophy = displayType === VERTICAL_DISPLAY_TYPE.FULL ? true : false;
  const isActivationTermsTextCenter = displayType === VERTICAL_DISPLAY_TYPE.FULL ? true : false;
  const loadingButtonSize = displayType === VERTICAL_DISPLAY_TYPE.FULL ? 'md' : 'sm';
  const styledCardPaddingX =
    displayType === VERTICAL_DISPLAY_TYPE.FULL ? { default: 'xl', sm_lte: 'sm' } : { default: 'sm' };

  const title = 'Simplify your rental process';

  const handleEnableApplicationsClick = () => {
    if (canAccessBusinessVerificationLite && !isApplicationManager && user.isPropertyManagerCompanyAdmin) {
      setShowAppsPermissionModal(true);
    } else {
      setIsEnableApplicationsLoading(true);

      dispatch(PropertiesActions.isApplicationsAccepted(listingId))
        // @ts-expect-error
        .then(() => {
          refetchGetNextStepsSummary();
          queryClient.invalidateQueries({ queryKey: ['getApplicationsSummary', listingId] });
          enqueueToast(<Toast tone="success">Applications have been enabled on your listing</Toast>);

          refetchWebAnalytics()
            .then((response) => {
              ApplicationsCSEvents.ClickEnableApplications({
                currentProperty: response.data ?? {},
                user,
                triggerLocation,
              });
            })
            .catch((error) =>
              logError({
                error,
                context: 'ApplicationsDisabledCard',
              }),
            );
        })
        .catch(() => {
          setFailureBanner({
            title: null,
            body: <Text>There was an error enabling applications.</Text>,
          });
        })
        .finally(() => setIsEnableApplicationsLoading(false));
    }
  };

  const renderBodyContent = useMemo(() => {
    return (
      <>
        Save time with Zillow&apos;s online rental applications. Access credit reports, background checks and eviction
        history in one place.{' '}
        <Anchor as="a" href={tenantScreeningPage} target="_blank">
          Learn more
        </Anchor>
      </>
    );
  }, []);

  const renderEnableApplicationsButton = useMemo(() => {
    return (
      <Button
        data-testid="EnableApplicationsButton"
        fluid
        tone="brand"
        emphasis={status.toLowerCase() === 'off-market' ? 'outlined' : 'filled'}
        size={loadingButtonSize}
        onClick={handleEnableApplicationsClick}
        disabled={!canEnableApplications}
        loading={isEnableApplicationsLoading}
        data-eventid={ApplicationsCSEvents.ClickEnableApplications.data_event_id}
      >
        Enable applications
      </Button>
    );
  }, [isApplicationManager]);

  const renderTerms = useMemo(() => {
    return (
      <div data-testid="Terms-Container">
        {canEnableApplications ? (
          <ListingActivationTerm
            actionText="By enabling or sharing applications"
            isTextCenter={isActivationTermsTextCenter}
          />
        ) : (
          <Paragraph textStyle="body-sm" css={{ marginTop: '600' }}>
            Why can&apos;t I enable applications{' '}
            <TooltipPopper triggered={<Tooltip body={<Text>{accessText}</Text>} />} placement="top" />
          </Paragraph>
        )}
      </div>
    );
  }, []);

  const renderFullType = useMemo(() => {
    return (
      <StyledEmptySimpleCard
        data-testid="ApplicationsDisabledCard-Full"
        paddingX={styledCardPaddingX}
        cardType="secondary"
        cardElevation={0}
      >
        <StyledEmptyStateContainer>
          <StyledEmptyIconContainer>{displayIconTrophy && <DetailedIconTrophy />}</StyledEmptyIconContainer>

          <EmptyStateTitleText asChild>
            <h4>{title}</h4>
          </EmptyStateTitleText>

          <EmptyStateBodyText asChild css={{ textAlign: 'center' }}>
            <p>{renderBodyContent}</p>
          </EmptyStateBodyText>

          <Flex display="flex" alignItems={alignItems} flexDirection="column">
            <StyledCTAContainerSpacer data-testid="CTAContainer" marginBottom="md">
              {renderEnableApplicationsButton}

              <Spacer as="div" marginBottom="xs" />

              {(canAccessBusinessVerificationLite ? isApplicationManager : true) && (
                <ApplicationShareLinks
                  displayType={displayType}
                  isTertiaryButtonStyling={true}
                  applicationsSummary={applicationsSummary}
                />
              )}
            </StyledCTAContainerSpacer>

            {renderTerms}
          </Flex>
        </StyledEmptyStateContainer>
      </StyledEmptySimpleCard>
    );
  }, [renderEnableApplicationsButton, renderTerms]);

  const renderSimpleType = useMemo(() => {
    return (
      <SimpleCard data-testid="ApplicationsDisabledCard-Simple">
        <Text asChild textStyle="body-bold">
          <p>{title}</p>
        </Text>

        <Spacer marginBottom="sm">
          <Text asChild textStyle="body-sm">
            <p>{renderBodyContent}</p>
          </Text>
        </Spacer>

        <Flex data-testid="CTAContainer" display="flex" alignItems={'center'} flexDirection="row">
          <Spacer marginRight="xs">{renderEnableApplicationsButton}</Spacer>

          {(canAccessBusinessVerificationLite ? isApplicationManager : true) && (
            <ApplicationShareLinks
              displayType={displayType}
              isTertiaryButtonStyling={true}
              applicationsSummary={applicationsSummary}
            />
          )}
        </Flex>

        {renderTerms}
      </SimpleCard>
    );
  }, [renderEnableApplicationsButton, renderTerms]);

  if (failureBanner.body) {
    return <AlertWithTitle appearance="error" body={failureBanner.body} title={failureBanner.title} />;
  }

  return (
    <div data-testid="ApplicationsDisabledCard">
      {displayType === VERTICAL_DISPLAY_TYPE.FULL ? renderFullType : renderSimpleType}
    </div>
  );
};

export default ApplicationsDisabledCard;
